import React, { useState } from 'react';
import { CloudUpload, X } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { showErrorToast } from '../utils/toast';

const AddLoanNote = () => {
  const { loanId } = useParams();
  const [note, setNote] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
  
    try {
      const formData = new FormData();
      formData.append('note', note);
      formData.append('loanApplicationId', loanId);

  
      // Convert document types to JSON string
      const documentTypes = uploadedFiles.map(file => file.documentType);
      formData.append('documentTypes', JSON.stringify(documentTypes));
  
      // Append each file with the field name 'documents'
      uploadedFiles.forEach((fileData) => {
        formData.append('documents', fileData.file);
      });
  
      await axiosInstance.post(`/loan/loans/notes`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
  
      navigate(-1);
    } catch (error) {
      showErrorToast(error.message || 'Error creating loan note');
    } finally {
      setLoading(false);
    }
  };
  
  
  
  

  // Rest of the component remains the same
  const handleFileChange = async (e) => {
    setUploading(true);
    try {
      const files = Array.from(e.target.files);
      const newFiles = files.map(file => ({
        file,
        fileName: file.name,
        documentType: ''
      }));
      setUploadedFiles(prev => [...prev, ...newFiles]);
    } catch (error) {
      showErrorToast('Error uploading files');
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentTypeChange = (index, value) => {
    setUploadedFiles(prev =>
      prev.map((file, i) =>
        i === index ? { ...file, documentType: value } : file
      )
    );
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg mb-6">
      <h2 className="text-xl font-semibold mb-4">Add New Loan Note</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Note
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={4}
          />
        </div>

        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Documents
          </label>
          
          <div className="relative">
  <label className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer">
    <CloudUpload className="h-4 w-4 text-gray-400" />
    {uploading ? 'Uploading...' : 'Upload Files'}
    <input
      type="file"
      onChange={handleFileChange}
      className="hidden"
      disabled={uploading}
      multiple
      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx" // Added Excel formats
    />
  </label>
  <p className="text-xs text-gray-500 mt-1">
    Accepted formats: PDF, Word, JPEG, PNG, Excel (.xls, .xlsx)
  </p>
</div>


          {uploadedFiles.length > 0 && (
            <div className="space-y-4">
              {uploadedFiles.map((fileData, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div className="flex items-start justify-between">
                    <p className="text-sm font-medium text-gray-700">{fileData.fileName}</p>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div className="flex flex-col space-y-1">
                    <label className="text-xs font-medium text-gray-600">
                      Document Type
                    </label>
                    <input
                      type="text"
                      value={fileData.documentType}
                      onChange={(e) => handleDocumentTypeChange(index, e.target.value)}
                      placeholder="Enter document type"
                      required
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Note'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddLoanNote;